import Konva from 'konva';
import { useCallback, useEffect } from 'react';
import { CANVAS_HEIGHT, CANVAS_WIDTH } from '../constants';
import { useCanvasStore } from '../store/canvasStore';
import type { AnimationProps, AnimationType } from '../types';
import { clampToHex, parseColor } from '../utils/canvas';

interface AnimatedImage {
  id: string;
  animation: AnimationType;
  deleted?: boolean;
  animationDelay?: number;
  animationDuration?: number;
  opacity?: number;
  zIndex?: number;
}

export const useAnimation = () => {
  const previewMode = useCanvasStore((s) => s.previewMode);
  const canvasImages = useCanvasStore((s) => s.canvasImages);
  const stageRef = useCanvasStore((s) => s.stageRef);

  const createAnimation = useCallback(
    (node: Konva.Node, animationType: AnimationType, props: AnimationProps): Konva.Animation | null => {
      if (!animationType || !node) return null;

      const layer = node.getLayer();
      if (!layer) return null;

      let animation: Konva.Animation | null = null;
      const duration = props.duration ? Math.min(Math.max(Number(props.duration), 100), 10000) : 1000;

      switch (animationType) {
        case 'fade-in':
          animation = new Konva.Animation((frame) => {
            if (!frame) return;
            const opacity = 0.4 + 0.6 * Math.abs(Math.sin(frame.time / 2000));
            node.opacity(opacity);
          }, layer);
          break;

        case 'slide-up': {
          const initialY = node.y();
          animation = new Konva.Animation((frame) => {
            if (!frame) return;
            const offset = 10 * Math.sin(frame.time / 1500);
            node.y(initialY - offset);
          }, layer);
          break;
        }

        case 'zoom-in':
          animation = new Konva.Animation((frame) => {
            if (!frame) return;
            const scale = 0.9 + 0.2 * Math.abs(Math.sin(frame.time / 1250));
            node.scale({ x: scale, y: scale });
          }, layer);
          break;

        case 'simple-fade':
          node.opacity(0);
          animation = new Konva.Animation((frame) => {
            if (!frame || frame.time > duration) return;
            const progress = Math.min(1, frame.time / duration);
            node.opacity(progress);
            if (progress >= 1 && animation) animation.stop();
          }, layer);
          break;

        case 'slide-from-left': {
          const originalX = node.x();
          const nodeWidth =
            node instanceof Konva.Text ? node.width() : node instanceof Konva.Image ? node.width() : 100;
          node.x(-nodeWidth);

          animation = new Konva.Animation((frame) => {
            if (!frame || frame.time > duration) return;
            const progress = Math.min(1, frame.time / duration);
            const ease = 1 - (1 - progress) ** 3;
            node.x(-nodeWidth + (originalX + nodeWidth) * ease);
            if (progress >= 1 && animation) animation.stop();
          }, layer);
          break;
        }

        case 'slide-from-right': {
          const targetX = node.x();
          const elementWidth =
            node instanceof Konva.Text ? node.width() : node instanceof Konva.Image ? node.width() : 100;
          node.x(CANVAS_WIDTH);

          animation = new Konva.Animation((frame) => {
            if (!frame || frame.time > duration) return;
            const progress = Math.min(1, frame.time / duration);
            const ease = 1 - (1 - progress) ** 3;
            node.x(CANVAS_WIDTH - (CANVAS_WIDTH - targetX) * ease);
            if (progress >= 1 && animation) animation.stop();
          }, layer);
          break;
        }

        case 'slide-from-bottom': {
          const finalY = node.y();
          node.y(CANVAS_HEIGHT);

          animation = new Konva.Animation((frame) => {
            if (!frame || frame.time > duration) return;
            const progress = Math.min(1, frame.time / duration);
            const ease = 1 - (1 - progress) ** 3;
            node.y(CANVAS_HEIGHT - (CANVAS_HEIGHT - finalY) * ease);
            if (progress >= 1 && animation) animation.stop();
          }, layer);
          break;
        }

        case 'rotate':
          animation = new Konva.Animation((frame) => {
            if (!frame) return;
            const angle = ((frame.time % 6000) / 6000) * 360;
            node.rotation(angle);
          }, layer);
          break;

        case 'bounce': {
          const bounceY = node.y();
          animation = new Konva.Animation((frame) => {
            if (!frame) return;
            const time = frame.time / 1000;
            const amplitude = 15 * (0.8 + 0.2 * Math.sin(time / 2));
            const bounce = -Math.abs(Math.sin(time * 3)) * amplitude;
            node.y(bounceY + bounce);
          }, layer);
          break;
        }

        case 'color-pulse':
          if (props.color && node instanceof Konva.Text) {
            const originalColor = parseColor(props.color);

            animation = new Konva.Animation((frame) => {
              if (!frame) return;
              const time = frame.time / 3000;
              const r = originalColor.r + Math.sin(time) * 40;
              const g = originalColor.g + Math.sin(time + 2) * 40;
              const b = originalColor.b + Math.sin(time + 4) * 40;

              const hexColor = `#${clampToHex(r)}${clampToHex(g)}${clampToHex(b)}`;
              node.fill(hexColor);
            }, layer);
          }
          break;

        case 'move': {
          const originalX = node.x();
          node.opacity(0);
          node.x(originalX - 50);

          animation = new Konva.Animation((frame) => {
            if (!frame || frame.time > duration) return;
            const progress = Math.min(1, frame.time / duration);
            const ease = 1 - (1 - progress) ** 2;

            node.x(originalX - 50 * (1 - ease));
            node.opacity(ease);

            if (progress >= 1 && animation) animation.stop();
          }, layer);
          break;
        }

        case 'flip-horizontal': {
          const originalScaleX = node.scaleX();

          animation = new Konva.Animation((frame) => {
            if (!frame) return;
            const progress = (frame.time % duration) / duration;

            const scale = originalScaleX * Math.cos(Math.PI * progress);
            node.scaleX(scale);

            if (progress > 0.9) {
              const bounce = Math.sin((progress - 0.9) * 10 * Math.PI) * 0.1;
              node.scaleX(scale * (1 + bounce));
            }
          }, layer);
          break;
        }

        case 'flip-vertical': {
          const originalScaleY = node.scaleY();

          animation = new Konva.Animation((frame) => {
            if (!frame) return;
            const progress = (frame.time % duration) / duration;

            const scale = originalScaleY * Math.cos(Math.PI * progress);
            node.scaleY(scale);

            if (progress > 0.9) {
              const bounce = Math.sin((progress - 0.9) * 10 * Math.PI) * 0.1;
              node.scaleY(scale * (1 + bounce));
            }
          }, layer);
          break;
        }

        case 'pulse':
          animation = new Konva.Animation((frame) => {
            if (!frame) return;
            const scale = 1 + 0.1 * Math.sin(frame.time / (duration / 2));
            node.scale({ x: scale, y: scale });
          }, layer);
          break;

        case 'spin':
          animation = new Konva.Animation((frame) => {
            if (!frame) return;
            const angle = ((frame.time % (duration * 2)) / (duration * 2)) * 360;
            node.rotation(angle);
          }, layer);
          break;

        case 'swing':
          animation = new Konva.Animation((frame) => {
            if (!frame) return;
            const angle = 20 * Math.sin((frame.time / duration) * Math.PI);
            node.rotation(angle);
          }, layer);
          break;

        case 'float': {
          const floatY = node.y();

          animation = new Konva.Animation((frame) => {
            if (!frame) return;
            const offset = 15 * Math.sin(frame.time / (duration * 1.5));
            node.y(floatY + offset);
          }, layer);
          break;
        }

        case 'wiggle': {
          const wiggleX = node.x();

          animation = new Konva.Animation((frame) => {
            if (!frame) return;
            const offset = 8 * Math.sin(frame.time / (duration / 4));
            node.x(wiggleX + offset);
          }, layer);
          break;
        }

        case 'typewriter':
          // Only for text nodes
          if (!(node instanceof Konva.Text)) break;
          {
            const fullText = node.text();
            const textLength = Math.min(1000, fullText.length); // Security: limit text length
            node.text('');

            animation = new Konva.Animation((frame) => {
              if (!frame) return;
              const progress = Math.min(1, frame.time / duration);
              const charIndex = Math.floor(progress * textLength);

              if (charIndex <= textLength) {
                node.text(fullText.substring(0, charIndex));
              }

              if (progress >= 1 && animation) animation.stop();
            }, layer);
          }
          break;

        case 'blur-in': {
          node.opacity(0);
          node.scale({ x: 1.2, y: 1.2 });

          animation = new Konva.Animation((frame) => {
            if (!frame || frame.time > duration) return;
            const progress = Math.min(1, frame.time / duration);
            const ease = 1 - (1 - progress) ** 2;

            node.opacity(ease);
            node.scale({
              x: 1.2 - 0.2 * ease,
              y: 1.2 - 0.2 * ease,
            });

            if (progress >= 1 && animation) animation.stop();
          }, layer);
          break;
        }

        case 'grow': {
          node.scale({ x: 0.3, y: 0.3 });
          node.opacity(0.5);

          animation = new Konva.Animation((frame) => {
            if (!frame || frame.time > duration) return;
            const progress = Math.min(1, frame.time / duration);
            const ease = 1 - (1 - progress) ** 2;

            node.scale({
              x: 0.3 + 0.7 * ease,
              y: 0.3 + 0.7 * ease,
            });
            node.opacity(0.5 + 0.5 * ease);

            if (progress >= 1 && animation) animation.stop();
          }, layer);
          break;
        }

        case 'shrink': {
          node.scale({ x: 1.2, y: 1.2 });

          animation = new Konva.Animation((frame) => {
            if (!frame || frame.time > duration) return;
            const progress = Math.min(1, frame.time / duration);

            node.scale({
              x: 1.2 - 0.2 * progress,
              y: 1.2 - 0.2 * progress,
            });

            if (progress >= 1 && animation) animation.stop();
          }, layer);
          break;
        }

        case 'slide-from-top': {
          const endY = node.y();
          node.y(-node.height());

          animation = new Konva.Animation((frame) => {
            if (!frame || frame.time > duration) return;
            const progress = Math.min(1, frame.time / duration);
            const ease = 1 - (1 - progress) ** 3;

            node.y(-node.height() + (endY + node.height()) * ease);

            if (progress >= 1 && animation) animation.stop();
          }, layer);
          break;
        }

        case 'rubber-band': {
          animation = new Konva.Animation((frame) => {
            if (!frame || frame.time > duration) return;
            const progress = Math.min(1, frame.time / duration);

            let scaleX = 1;
            let scaleY = 1;

            if (progress < 0.3) {
              scaleX = 1 + progress * 2;
              scaleY = 1 - progress * 0.5;
            } else if (progress < 0.6) {
              scaleX = 1.6 - (progress - 0.3) * 2;
              scaleY = 0.85 + (progress - 0.3) * 0.5;
            } else if (progress < 0.8) {
              scaleX = 1 + (progress - 0.6) * 0.6;
              scaleY = 1 - (progress - 0.6) * 0.3;
            } else {
              scaleX = 1.12 - (progress - 0.8) * 0.6;
              scaleY = 0.94 + (progress - 0.8) * 0.3;
            }

            node.scale({ x: scaleX, y: scaleY });

            if (progress >= 1 && animation) {
              node.scale({ x: 1, y: 1 });
              animation.stop();
            }
          }, layer);
          break;
        }

        case 'jello': {
          animation = new Konva.Animation((frame) => {
            if (!frame || frame.time > duration) return;
            const progress = Math.min(1, frame.time / duration);

            let skewX = 0;

            if (progress < 0.22) {
              skewX = Math.sin(progress * 4.5 * Math.PI) * 12;
            } else if (progress < 0.44) {
              skewX = Math.sin(progress * 4.5 * Math.PI) * -10;
            } else if (progress < 0.66) {
              skewX = Math.sin(progress * 4.5 * Math.PI) * 8;
            } else if (progress < 0.88) {
              skewX = Math.sin(progress * 4.5 * Math.PI) * -6;
            } else {
              skewX = Math.sin(progress * 4.5 * Math.PI) * 4;
            }

            node.skewX(skewX);

            if (progress >= 1 && animation) {
              node.skewX(0);
              animation.stop();
            }
          }, layer);
          break;
        }

        default:
          return null;
      }

      animation?.start();
      return animation;
    },
    []
  );

  // Add image animations to the animation preview system
  useEffect(() => {
    if (!previewMode) return;

    const animationIds: number[] = [];
    const initialStates = new Map();

    // Process image animations
    canvasImages.forEach((image) => {
      const typedImage = image as AnimatedImage;

      if (!image.animation || image.animation === 'none' || typedImage.deleted) return;

      const imageNode = stageRef.current?.findOne(`#${image.id}`);
      if (!imageNode) return;

      const konvaNode = imageNode as unknown as Konva.Image;

      // Store initial state to restore later
      initialStates.set(image.id, {
        x: konvaNode.x(),
        y: konvaNode.y(),
        opacity: konvaNode.opacity() || 1,
        scale: { x: konvaNode.scaleX() || 1, y: konvaNode.scaleY() || 1 },
        rotation: konvaNode.rotation() || 0,
        visible: konvaNode.visible(),
      });

      const delay = Math.min(5000, Math.max(0, (typedImage.animationDelay || 0) * 1000));
      const duration = Math.min(10000, Math.max(100, (typedImage.animationDuration || 1) * 1000));

      // If we have a delay, hide the node initially
      if (delay > 0) {
        konvaNode.visible(false);
        konvaNode.opacity(0);

        const layer = konvaNode.getLayer();
        if (layer) layer.batchDraw();
      }

      // Setup specific animation preparations based on animation type
      setTimeout(() => {
        // Make the node visible when delay period is over
        konvaNode.visible(true);

        // Apply specific starting states based on animation type
        if (image.animation === 'simple-fade') {
          konvaNode.opacity(0);
        } else {
          konvaNode.opacity(typedImage.opacity || 1);
        }

        // Reset scale for animations that need it
        if (image.animation === 'zoom-in') {
          konvaNode.scaleX(0.8);
          konvaNode.scaleY(0.8);
        } else if (image.animation.includes('slide-from')) {
          konvaNode.scaleX(1);
          konvaNode.scaleY(1);
        }

        // Start the animation
        const anim = createAnimation(konvaNode, image.animation as AnimationType, {
          duration,
          ...image,
        });

        if (anim) animationIds.push(anim.id);

        const layer = konvaNode.getLayer();
        if (layer) layer.batchDraw();
      }, delay);
    });

    return () => {
      animationIds.forEach((id) => {
        Konva.Animation.animations.forEach((a) => {
          if (a.id === id) a.stop();
        });
      });

      initialStates.forEach((state, id) => {
        const node = stageRef.current?.findOne(`#${id}`);
        if (node) {
          const konvaNode = node as unknown as Konva.Node;
          konvaNode.x(state.x);
          konvaNode.y(state.y);
          konvaNode.opacity(state.opacity);
          konvaNode.scaleX(state.scale.x);
          konvaNode.scaleY(state.scale.y);
          konvaNode.rotation(state.rotation);
          konvaNode.visible(state.visible);
        }
      });

      // Redraw all layers to ensure clean state
      stageRef.current?.getLayers().forEach((layer) => layer.batchDraw());
    };
  }, [previewMode, canvasImages, stageRef, createAnimation]);

  return { createAnimation };
};

export default useAnimation;
